/*
 * Signing in: crew sign in with a texted code first (/sign-in, English or Spanish), the office below it with email and
 * password (/sign-in/office), two-step (setting it up, then the code each time), and accepting an invite. Owner, Oct 31:
 * there are always more crew than office, so the crew way comes first. Client-facing style: a full-width color band on
 * top in the brand color, no card floating on gray.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import QRCode from "qrcode";
import { useEffect, useState, type ReactNode } from "react";
import { api, ApiError, setCompany } from "../../api/client";
import { useSignInLook, type InvitePeek, type SignInAnswer } from "../../api/hooks";
import { InputText } from "../../components/fields";
import { afterFullSignIn, leavingForSso, markSignedIn, safeNext, stopSso } from "../../app/sso";
import { Banner, Button, Loading, ROLE_WORDS } from "../../components/parts";

/**
 * At a brand's own web address the band shows that brand (its color, logo and name); elsewhere it is the plain Steward band.
 * The sign in pages (sso) first check head office's address for this browser's shared sign in (app/sso.ts).
 */
export function AuthFrame({ children, brand, sso }: { children: ReactNode; brand?: string; sso?: boolean }) {
  const signInLook = useSignInLook();
  const look = signInLook.data?.brand ?? null;
  const color = brand ?? look?.color ?? undefined;
  // Buttons and links use --brand, set on the page root (the signed-in app sets its company's the same way), so they
  // match the band, not the plain Steward green.
  useEffect(() => {
    if (color) document.documentElement.style.setProperty("--brand", color);
  }, [color]);
  // Wait for this address's brand so the band doesn't show the default green first.
  if (signInLook.isLoading && (!brand || sso)) return <Loading />;
  if (sso && leavingForSso(signInLook.data?.sso)) return <Loading />;
  return (
    <div className="auth">
      <div className="auth-band" style={color ? { background: color } : undefined}>
        {/* The white logo sits right on the color; the color logo needs its white tile. */}
        {look?.logoWhiteUrl ? (
          <img className="auth-logo white" src={look.logoWhiteUrl} alt="" />
        ) : look?.logoUrl ? (
          <img className="auth-logo" src={look.logoUrl} alt="" />
        ) : (
          <span className="logo" aria-hidden>
            {(look?.name ?? "Steward").replace(/^The /, "").charAt(0)}
          </span>
        )}
        <b>{look?.name ?? "Steward"}</b>
      </div>
      <div className="auth-main">
        <div className="auth-panel">{children}</div>
      </div>
      <div className="auth-foot">The Trades · New Orleans</div>
    </div>
  );
}

const errorText = (e: unknown) => (e instanceof ApiError ? e.message : "That didn't work. Try again.");

function useAfterSignIn() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  return async (answer: SignInAnswer) => {
    qc.clear();
    if (answer.twoStep === "done") await afterFullSignIn(() => navigate({ to: "/" }));
    else await navigate({ to: "/two-step", search: { mode: answer.twoStep } });
  };
}

/** Owners and the office: email and password, then two-step. */
export function OfficeSignIn() {
  const [email, setEmail] = useState(__PREVIEW__ ? "owner@example.com" : "");
  const [password, setPassword] = useState(__PREVIEW__ ? "preview-password" : "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const after = useAfterSignIn();

  const submit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await after(await api<SignInAnswer>("POST", "auth/sign-in", { email, password, kind: "web" }));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame sso>
      <div>
        <h1>Office sign in</h1>
        <p className="lead">For owners and the office. Use the email and password you picked.</p>
      </div>
      {error ? <Banner type="error">{error}</Banner> : null}
      <form onSubmit={(e) => void submit(e)}>
        <InputText
          label="Email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <InputText
          label="Password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Button submit size="large" fullWidth loading={busy}>
          Sign in
        </Button>
      </form>
      <div className="auth-alt">
        <Link to="/forgot" search={email ? { email } : {}}>
          Forgot password?
        </Link>
        <Link to="/sign-in">On the crew? Sign in with your phone</Link>
        <Link to="/privacy" className="muted small">
          Privacy policy
        </Link>
      </div>
    </AuthFrame>
  );
}

/** Forgot password: asks for the email, always answers the same, so it never says who has a login. */
export function ForgotPassword() {
  const search: { email?: string } = useSearch({ strict: false });
  const [email, setEmail] = useState(search.email ?? "");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("POST", "auth/forgot", { email });
      setSent(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <AuthFrame>
        <div>
          <h1>Check your email</h1>
          <p className="lead">
            If {email} has a login here, we sent it a link to pick a new password. The link works for 1 hour. Check spam if it
            doesn&apos;t show up in a few minutes.
          </p>
        </div>
        <Link to="/sign-in/office" className="btn secondary large full">
          Back to sign in
        </Link>
      </AuthFrame>
    );
  }
  return (
    <AuthFrame>
      <div>
        <h1>Forgot your password?</h1>
        <p className="lead">
          Type the email you sign in with. We&apos;ll send a link to pick a new password. New to this Steward? This works the same
          way.
        </p>
      </div>
      {error ? <Banner type="error">{error}</Banner> : null}
      <form onSubmit={(e) => void submit(e)}>
        <InputText
          label="Email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Button submit size="large" fullWidth loading={busy}>
          Send me a link
        </Button>
      </form>
      <div className="auth-alt">
        <Link to="/sign-in/office">Back to sign in</Link>
        <Link to="/sign-in">On the crew? Sign in with your phone</Link>
      </div>
    </AuthFrame>
  );
}

/** The emailed link: pick a new password. Two-step stays on, so the next sign in still asks for the app code. */
/** The code a company switch brought, traded once even when React runs the page's effect twice. */
let handoff: Promise<{ companyId: string; back: string | null; next: string | null }> | null = null;

/**
 * The new tab a company switch opens at a brand's address (auth/handoff on the server). It trades the one-time code for a
 * session here, picks the company, and opens the app. The code leaves the address bar before anything else happens.
 * Shared sign in (app/sso.ts) comes here too: from head office's address with the page to open (next), or to head office's
 * address right after signing in elsewhere, with the page to go back to (back).
 */
export function Handoff() {
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!handoff) {
      const hash = new URLSearchParams(window.location.hash.slice(1));
      const code = hash.get("code") ?? "";
      const back = hash.get("back");
      const next = hash.get("next");
      window.history.replaceState(null, "", "/handoff");
      handoff = api<{ companyId: string; back: string | null }>("POST", "auth/handoff/use", {
        code,
        ...(back ? { back } : {}),
      }).then((a) => ({ ...a, next }));
    }
    let live = true;
    handoff.then(
      ({ companyId, back, next }) => {
        markSignedIn();
        if (back) {
          window.location.replace(back);
          return;
        }
        setCompany(companyId);
        window.location.replace(safeNext(next));
      },
      (err: unknown) => {
        stopSso();
        if (live) setError(errorText(err));
      },
    );
    return () => {
      live = false;
    };
  }, []);

  if (!error) return <Loading />;
  return (
    <AuthFrame>
      <div>
        <h1>That didn't open</h1>
        <p className="lead">{error}</p>
      </div>
      <Link to="/sign-in" className="btn primary large full">
        Sign in
      </Link>
    </AuthFrame>
  );
}

export function ResetPassword({ token }: { token: string }) {
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    if (password !== again) {
      setError("The two passwords don't match. Type the same one twice.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api("POST", "auth/reset", { token, password });
      setDone(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <AuthFrame>
        <div>
          <h1>Your password is set</h1>
          <p className="lead">
            Sign in with your email and the new password. If you use an authenticator app, it still asks for the code, same as
            before.
          </p>
        </div>
        <Link to="/sign-in/office" className="btn primary large full">
          Sign in
        </Link>
      </AuthFrame>
    );
  }
  return (
    <AuthFrame>
      <div>
        <h1>Pick a new password</h1>
        <p className="lead">This signs you out on every device. Your authenticator app keeps working.</p>
      </div>
      {error ? <Banner type="error">{error}</Banner> : null}
      <form onSubmit={(e) => void submit(e)}>
        <InputText
          label="New password"
          type="password"
          autoComplete="new-password"
          minLength={10}
          required
          description="At least 10 characters. A short sentence works well."
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <InputText
          label="Type it again"
          type="password"
          autoComplete="new-password"
          minLength={10}
          required
          value={again}
          onChange={(e) => setAgain(e.target.value)}
        />
        <Button submit size="large" fullWidth loading={busy}>
          Save password
        </Button>
      </form>
      <div className="auth-alt">
        <Link to="/forgot">Link run out? Ask for a new one</Link>
      </div>
    </AuthFrame>
  );
}

export function TwoStep() {
  const { mode } = useSearch({ strict: false });
  return mode === "setup" ? <TwoStepSetup /> : <TwoStepCode />;
}

function TwoStepCode() {
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const submit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("POST", "auth/two-step", { code });
      await afterFullSignIn(() => navigate({ to: "/" }));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthFrame>
      <div>
        <h1>{backup ? "Use a backup code" : "Enter your code"}</h1>
        <p className="lead">
          {backup
            ? "Type one of the backup codes you saved. Each one works once."
            : "Open your authenticator app and type the 6 digits it shows for Steward."}
        </p>
      </div>
      {error ? <Banner type="error">{error}</Banner> : null}
      {__PREVIEW__ ? <Banner>This is a preview, so any 6 digits work.</Banner> : null}
      <form onSubmit={(e) => void submit(e)}>
        <InputText
          label={backup ? "Backup code" : "6-digit code"}
          className={backup ? undefined : "code-input"}
          inputMode={backup ? "text" : "numeric"}
          autoComplete="one-time-code"
          maxLength={backup ? 12 : 6}
          required
          value={code}
          onChange={(e) => setCode(backup ? e.target.value : e.target.value.replace(/\D/g, ""))}
        />
        <Button submit size="large" fullWidth loading={busy}>
          Continue
        </Button>
      </form>
      <div className="auth-alt">
        <button
          type="button"
          className="btn tertiary small"
          onClick={() => {
            setBackup((b) => !b);
            setCode("");
          }}
        >
          {backup ? "Use my authenticator app" : "Lost your phone? Use a backup code"}
        </button>
      </div>
    </AuthFrame>
  );
}

function TwoStepSetup() {
  const [setup, setSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api<{ secret: string; otpauthUrl: string }>("POST", "auth/two-step/setup").then(
      (s) => {
        setSetup(s);
        void QRCode.toDataURL(s.otpauthUrl, { margin: 0, width: 320 }).then(setQr);
      },
      (e: unknown) => setError(errorText(e)),
    );
  }, []);

  const confirm = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setCodes((await api<{ backupCodes: string[] }>("POST", "auth/two-step/confirm", { code })).backupCodes);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  if (codes) {
    return (
      <AuthFrame>
        <div>
          <h1>Save your backup codes</h1>
          <p className="lead">
            If you lose your phone, each code lets you in once. Keep them somewhere safe. You won't see them again.
          </p>
        </div>
        <div className="backup-codes num">
          {codes.map((c) => (
            <span key={c}>{c}</span>
          ))}
        </div>
        <div className="row">
          <Button type="secondary" onClick={() => void navigator.clipboard.writeText(codes.join("\n")).catch(() => null)}>
            Copy codes
          </Button>
        </div>
        <Button size="large" fullWidth onClick={() => void afterFullSignIn(() => navigate({ to: "/" }))}>
          I saved them
        </Button>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame>
      <div>
        <h1>Set up two-step sign in</h1>
        <p className="lead">
          Owners and managers sign in with a password and a code from their phone. It keeps the business safe if a password leaks.
        </p>
      </div>
      {error ? <Banner type="error">{error}</Banner> : null}
      <div className="stack">
        <b>1. Scan this with an authenticator app</b>
        <p className="muted">Google Authenticator, Microsoft Authenticator and 1Password all work.</p>
        <div className="qr">{qr ? <img src={qr} alt="Code to scan with your authenticator app" /> : null}</div>
        <p className="muted">Can't scan it? Type this key into the app instead.</p>
        <div className="secret">{setup?.secret.replace(/(.{4})/g, "$1 ").trim() ?? "Loading"}</div>
      </div>
      <form onSubmit={(e) => void confirm(e)}>
        <b>2. Type the 6 digits the app shows</b>
        {__PREVIEW__ ? <Banner>This is a preview, so any 6 digits work.</Banner> : null}
        <InputText
          label="6-digit code"
          className="code-input"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          required
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
        />
        <Button submit size="large" fullWidth loading={busy} disabled={!setup}>
          Turn on two-step
        </Button>
      </form>
    </AuthFrame>
  );
}

const CREW_SIGN_IN_WORDS = {
  en: {
    title: "Crew sign in",
    lead: "Type your phone number. We text you a 6-digit code. Nothing to remember.",
    phone: "Your phone number",
    ask: "Text me a code",
    sent: "If that number is on the crew list, a code is on its way. It can take a minute.",
    code: "The 6-digit code we texted you",
    go: "Sign in",
    again: "Use a different number, or send a new code",
    officeAsk: "Owner or office?",
    office: "Office sign in",
    preview: "This is a preview, so no text is sent. Use 123456.",
  },
  es: {
    title: "Entrada del equipo",
    lead: "Escribe tu número de teléfono. Te mandamos un código de 6 dígitos por mensaje. No tienes que recordar nada.",
    phone: "Tu número de teléfono",
    ask: "Envíame un código",
    sent: "Si ese número está en la lista del equipo, el código va en camino. Puede tardar un minuto.",
    code: "El código de 6 dígitos que te enviamos",
    go: "Entrar",
    again: "Usar otro número, o pedir un código nuevo",
    officeAsk: "¿Dueño u oficina?",
    office: "Entrada de oficina",
    preview: "Es una vista previa, no se envía ningún mensaje. Usa 123456.",
  },
} as const;

type Lang = "en" | "es";
const LANG_KEY = "steward.signInLang";

/** The language picked last time on this phone, else the phone's own language. */
function startLang(): Lang {
  try {
    const saved = localStorage.getItem(LANG_KEY);
    if (saved === "en" || saved === "es") return saved;
  } catch {
    // No storage (private window): use the phone's language.
  }
  return navigator.language.toLowerCase().startsWith("es") ? "es" : "en";
}

/**
 * The sign in page everyone lands on: crew sign in with a texted code, with English and Spanish on top. The office way
 * sits at the bottom. The language picked here also becomes the crew member's app language once they are in.
 */
export function SignIn() {
  const [lang, setLangState] = useState<Lang>(startLang);
  const [picked, setPicked] = useState(false);
  const w = CREW_SIGN_IN_WORDS[lang];
  const setLang = (l: Lang) => {
    setLangState(l);
    setPicked(true);
    try {
      localStorage.setItem(LANG_KEY, l);
    } catch {
      // Not saved; it still works for this visit.
    }
  };
  const [phone, setPhone] = useState(__PREVIEW__ ? "(504) 555-0103" : "");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const after = useAfterSignIn();

  const ask = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("POST", "auth/code", { phone });
      setSent(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const verify = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const answer = await api<SignInAnswer>("POST", "auth/code/verify", { phone, code, kind: "web" });
      // Spanish picked here (or English switched to on purpose) carries into the app.
      if (picked || lang === "es") await api("POST", "auth/language", { locale: lang }).catch(() => undefined);
      await after(answer);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame sso>
      <div className="lang-switch" role="group" aria-label="Language / Idioma">
        <button type="button" aria-pressed={lang === "en"} onClick={() => setLang("en")}>
          English
        </button>
        <button type="button" aria-pressed={lang === "es"} onClick={() => setLang("es")} lang="es">
          Español
        </button>
      </div>
      <div lang={lang}>
        <h1>{w.title}</h1>
        <p className="lead">{w.lead}</p>
      </div>
      {error ? <Banner type="error">{error}</Banner> : null}
      {!sent ? (
        <form onSubmit={(e) => void ask(e)} lang={lang}>
          <InputText
            label={w.phone}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            required
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <Button submit size="large" fullWidth loading={busy}>
            {w.ask}
          </Button>
        </form>
      ) : (
        <form onSubmit={(e) => void verify(e)} lang={lang}>
          <Banner type="success">{w.sent}</Banner>
          {__PREVIEW__ ? <Banner>{w.preview}</Banner> : null}
          <InputText
            label={w.code}
            className="code-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/D/g, ""))}
          />
          <Button submit size="large" fullWidth loading={busy}>
            {w.go}
          </Button>
          <button
            type="button"
            className="btn tertiary small"
            onClick={() => {
              setSent(false);
              setCode("");
            }}
          >
            {w.again}
          </button>
        </form>
      )}
      <div className="auth-office" lang={lang}>
        <span>{w.officeAsk}</span>
        <Link to="/sign-in/office" className="btn secondary large full">
          {w.office}
        </Link>
        <Link to="/privacy" className="muted small">
          {lang === "es" ? "Privacidad" : "Privacy policy"}
        </Link>
      </div>
    </AuthFrame>
  );
}

export function InviteAccept({ token }: { token: string }) {
  const peek = useQuery({
    queryKey: ["invite", token],
    queryFn: () => api<InvitePeek>("GET", `public/invites/${token}`),
    retry: false,
  });
  const [password, setPassword] = useState("");
  const [done, setDone] = useState<"password" | "code" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const accept = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = peek.data?.by === "email" ? { password } : {};
      setDone((await api<{ signIn: "password" | "code" }>("POST", `public/invites/${token}/accept`, body)).signIn);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  if (peek.isLoading) return <AuthFrame>Loading</AuthFrame>;
  if (peek.error || !peek.data) {
    return (
      <AuthFrame>
        <h1>This invite has run out</h1>
        <Banner type="warning">{errorText(peek.error)}</Banner>
      </AuthFrame>
    );
  }
  const p = peek.data;
  if (done) {
    return (
      <AuthFrame>
        <div>
          <h1>You're in, {p.firstName}</h1>
          <p className="lead">
            {done === "password"
              ? "Sign in with your email and the password you just picked."
              : "Sign in with your phone. We'll text you a code each time."}
          </p>
        </div>
        <Link to={done === "password" ? "/sign-in/office" : "/sign-in"} className="btn primary large full">
          Sign in
        </Link>
      </AuthFrame>
    );
  }
  return (
    <AuthFrame>
      <div>
        <h1>Join {p.companyName}</h1>
        <p className="lead">
          Hi {p.firstName}, you're invited as {(ROLE_WORDS[p.role] ?? p.role).toLowerCase()}.
          {p.by === "email" ? " Pick a password to finish." : " Accept, then sign in with your phone."}
        </p>
      </div>
      {error ? <Banner type="error">{error}</Banner> : null}
      <form onSubmit={(e) => void accept(e)}>
        {p.by === "email" ? (
          <InputText
            label="Pick a password"
            type="password"
            autoComplete="new-password"
            minLength={10}
            required
            description="At least 10 characters. A short sentence works well."
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        ) : null}
        <Button submit size="large" fullWidth loading={busy}>
          Accept invite
        </Button>
      </form>
    </AuthFrame>
  );
}
